"use client";

import { useEffect, useState, useCallback } from "react";
import Image from "next/image";
import { getProductImageUrl } from "@/lib/utils";
import { X, FileText, CheckCircle, Loader2 } from "lucide-react";
import type { StoreQuoteField } from "@/lib/validations/quote";

interface QuoteProduct {
  id: number;
  title: string;
  slug: string;
  store_name: string | null;
  image_local: string | null;
  sku: string | null;
}

interface QuoteRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: QuoteProduct;
}

export function QuoteRequestModal({
  isOpen,
  onClose,
  product,
}: QuoteRequestModalProps) {
  const [fields, setFields] = useState<StoreQuoteField[]>([]);
  const [loadingFields, setLoadingFields] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Base fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");

  // Dynamic field values: field_id -> string | string[]
  const [customValues, setCustomValues] = useState<
    Record<string, string | string[]>
  >({});

  // Fetch store fields and user info on open
  useEffect(() => {
    if (!isOpen) return;

    setSubmitted(false);
    setError(null);

    // Fetch custom fields for this store
    if (product.store_name) {
      setLoadingFields(true);
      fetch(
        `/api/quotes/store-fields?store_name=${encodeURIComponent(product.store_name)}`
      )
        .then((r) => r.json())
        .then((data) => {
          setFields(data.fields || []);
          // Initialize custom values
          const initial: Record<string, string | string[]> = {};
          for (const f of data.fields || []) {
            if (
              f.field_type === "checkbox_group"
            ) {
              initial[f.id] = [];
            } else {
              initial[f.id] = "";
            }
          }
          setCustomValues(initial);
        })
        .catch(() => setFields([]))
        .finally(() => setLoadingFields(false));
    } else {
      setFields([]);
      setLoadingFields(false);
    }

    // Try to pre-fill user info
    fetch("/api/auth/me")
      .then((r) => {
        if (r.ok) return r.json();
        return null;
      })
      .then((data) => {
        if (data?.user) {
          setFirstName(data.user.first_name || "");
          setLastName(data.user.last_name || "");
          setEmail(data.user.email || "");
          setPhone(data.user.phone || "");
        }
      })
      .catch(() => {});
  }, [isOpen, product.store_name]);

  // ESC to close
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleCheckboxChange = useCallback(
    (fieldId: number, option: string, checked: boolean) => {
      setCustomValues((prev) => {
        const current = (prev[fieldId] as string[]) || [];
        if (checked) {
          return { ...prev, [fieldId]: [...current, option] };
        } else {
          return {
            ...prev,
            [fieldId]: current.filter((v) => v !== option),
          };
        }
      });
    },
    []
  );

  const handleRadioChange = useCallback(
    (fieldId: number, value: string) => {
      setCustomValues((prev) => ({ ...prev, [fieldId]: value }));
    },
    []
  );

  const handleTextChange = useCallback(
    (fieldId: number, value: string) => {
      setCustomValues((prev) => ({ ...prev, [fieldId]: value }));
    },
    []
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Basic validation
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setError("Veuillez remplir les champs obligatoires (Prénom, Nom, Email).");
      return;
    }

    // Validate required custom fields
    for (const field of fields) {
      if (field.is_required) {
        const val = customValues[field.id];
        if (Array.isArray(val) && val.length === 0) {
          setError(`Le champ "${field.field_label}" est requis.`);
          return;
        }
        if (typeof val === "string" && !val.trim()) {
          setError(`Le champ "${field.field_label}" est requis.`);
          return;
        }
      }
    }

    setSubmitting(true);

    try {
      // Build custom_fields payload with only non-empty values
      const cleanCustom: Record<string, string | string[]> = {};
      for (const [key, val] of Object.entries(customValues)) {
        if (Array.isArray(val) && val.length > 0) {
          cleanCustom[key] = val;
        } else if (typeof val === "string" && val.trim()) {
          cleanCustom[key] = val;
        }
      }

      const res = await fetch("/api/quotes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product_id: product.id,
          store_name: product.store_name,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim(),
          phone: phone.trim() || null,
          message: message.trim() || null,
          custom_fields: cleanCustom,
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.error || "Erreur lors de l'envoi.");
        return;
      }

      setSubmitted(true);
    } catch {
      setError("Erreur de connexion. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-[9998]"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto pointer-events-auto animate-in zoom-in-95 fade-in duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-secondary px-5 py-3 flex items-center justify-between rounded-t-2xl sticky top-0 z-10">
            <div className="flex items-center gap-2 text-white text-sm font-medium">
              <FileText className="h-5 w-5" />
              Demander un devis
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {submitted ? (
            /* Success state */
            <div className="p-8 text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Demande envoyée !
              </h3>
              <p className="text-gray-600 mb-6">
                Votre demande de devis a bien été envoyée à{" "}
                <span className="font-medium">{product.store_name}</span>.
                Vous serez contacté dans les plus brefs délais.
              </p>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90 transition-colors"
              >
                Fermer
              </button>
            </div>
          ) : (
            <>
              {/* Product card */}
              <div className="p-5 flex gap-4 border-b">
                <div className="relative w-20 h-20 flex-shrink-0 bg-white rounded-lg overflow-hidden border">
                  {product.image_local ? (
                    <Image
                      src={getProductImageUrl(product.image_local)}
                      alt={product.title}
                      fill
                      className="object-contain p-2"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-50">
                      <FileText className="h-6 w-6 text-gray-300" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 line-clamp-2">
                    {product.title}
                  </h3>
                  {product.store_name && (
                    <p className="text-xs text-gray-400 mt-1 uppercase">
                      {product.store_name}
                    </p>
                  )}
                  {product.sku && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      Réf: {product.sku}
                    </p>
                  )}
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-5 space-y-4">
                {/* Error message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                {/* Base fields */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prénom <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary outline-none text-sm"
                      placeholder="Votre prénom"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary outline-none text-sm"
                      placeholder="Votre nom"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary outline-none text-sm"
                      placeholder="votre@email.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary outline-none text-sm"
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                </div>

                {/* Dynamic fields */}
                {loadingFields ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                  </div>
                ) : (
                  fields.map((field) => (
                    <DynamicField
                      key={field.id}
                      field={field}
                      value={customValues[field.id]}
                      onCheckboxChange={handleCheckboxChange}
                      onRadioChange={handleRadioChange}
                      onTextChange={handleTextChange}
                    />
                  ))
                )}

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message / Précisions
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary outline-none text-sm resize-none"
                    placeholder="Décrivez votre besoin..."
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 bg-secondary text-white rounded-lg font-medium hover:bg-secondary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    "Envoyer la demande de devis"
                  )}
                </button>
                <p className="text-xs text-gray-400 mt-3 text-center">
                  Vos données sont utilisées pour traiter votre demande. Consultez notre{" "}
                  <a href="/docs/mentions-legales.pdf" target="_blank" rel="noopener noreferrer" className="underline hover:text-secondary">politique de confidentialité</a>.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </>
  );
}

// Dynamic field renderer
function DynamicField({
  field,
  value,
  onCheckboxChange,
  onRadioChange,
  onTextChange,
}: {
  field: StoreQuoteField;
  value: string | string[] | undefined;
  onCheckboxChange: (fieldId: number, option: string, checked: boolean) => void;
  onRadioChange: (fieldId: number, value: string) => void;
  onTextChange: (fieldId: number, value: string) => void;
}) {
  const options: string[] = field.options || [];

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        {field.field_label}
        {field.is_required && <span className="text-red-500 ml-0.5">*</span>}
      </label>

      {field.field_type === "text" && (
        <input
          type="text"
          value={(value as string) || ""}
          onChange={(e) => onTextChange(field.id, e.target.value)}
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary outline-none text-sm"
        />
      )}

      {field.field_type === "textarea" && (
        <textarea
          value={(value as string) || ""}
          onChange={(e) => onTextChange(field.id, e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary outline-none text-sm resize-none"
        />
      )}

      {field.field_type === "number_select" && (
        <select
          value={(value as string) || "0"}
          onChange={(e) => onTextChange(field.id, e.target.value)}
          className="w-20 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary outline-none text-sm bg-white"
        >
          {Array.from({ length: 11 }, (_, i) => (
            <option key={i} value={String(i)}>
              {i}
            </option>
          ))}
        </select>
      )}

      {field.field_type === "select" && (
        <select
          value={(value as string) || ""}
          onChange={(e) => onTextChange(field.id, e.target.value)}
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary outline-none text-sm bg-white"
        >
          <option value="">Sélectionner...</option>
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      )}

      {field.field_type === "checkbox_group" && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {options.map((opt) => {
            const checked = Array.isArray(value) && value.includes(opt);
            return (
              <label
                key={opt}
                className={`flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer transition-colors text-sm ${
                  checked
                    ? "border-secondary bg-secondary/5 text-secondary"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) =>
                    onCheckboxChange(field.id, opt, e.target.checked)
                  }
                  className="sr-only"
                />
                <div
                  className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                    checked
                      ? "bg-secondary border-secondary"
                      : "border-gray-300"
                  }`}
                >
                  {checked && (
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </div>
                <span className="truncate">{opt}</span>
              </label>
            );
          })}
        </div>
      )}

      {field.field_type === "radio_group" && (
        <div className="flex flex-wrap gap-2">
          {options.map((opt) => {
            const selected = value === opt;
            return (
              <label
                key={opt}
                className={`flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer transition-colors text-sm ${
                  selected
                    ? "border-secondary bg-secondary/5 text-secondary"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <input
                  type="radio"
                  name={`field_${field.id}`}
                  checked={selected}
                  onChange={() => onRadioChange(field.id, opt)}
                  className="sr-only"
                />
                <div
                  className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${
                    selected
                      ? "border-secondary"
                      : "border-gray-300"
                  }`}
                >
                  {selected && (
                    <div className="w-2 h-2 rounded-full bg-secondary" />
                  )}
                </div>
                <span>{opt}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
